/**
 * Store Core Atoms - Base state management for store
 *
 * NO LOCAL TYPES - All types come from isomorphic/schemas/store
 * Follows the same pattern as workflows.ts
 */

import { atom } from "jotai";
import type {
  StoreListingWithDetails,
  StoreLicenseWithDetails,
  GetAvailableUpdatesResponse,
  GetListingResponse,
} from "@/isomorphic/schemas/store";
import type { AtomStatus, AtomError } from "@/frontend/types/atoms";

// ============================================================================
// BASE ATOMS (Private)
// ============================================================================

/**
 * Internal state atom: All store listings (raw state)
 * Private - do not use directly
 */
const _storeListingsStateAtom = atom<StoreListingWithDetails[]>([]);

/**
 * Internal state atom: Full listing details cache (with resource and reviews)
 * Private - do not use directly
 */
const _fullListingDetailsStateAtom = atom<Record<string, GetListingResponse>>({});

/**
 * Internal state atom: My licenses (raw state)
 * Private - do not use directly
 */
const _myLicensesStateAtom = atom<StoreLicenseWithDetails[]>([]);

/**
 * Internal state atom: Available updates (raw state)
 * Private - do not use directly
 */
const _availableUpdatesStateAtom = atom<GetAvailableUpdatesResponse["updates"]>([]);

/**
 * Internal atom: All store listings (pure state)
 * Private - use storeListingsAtom for read access
 */
export const _storeListingsAtom = atom(
  (get) => get(_storeListingsStateAtom),
  (get, set, newListings: StoreListingWithDetails[]) => {
    set(_storeListingsStateAtom, newListings);
  },
);

/**
 * Internal atom: Full listing details cache (with resource and reviews)
 * Private - use currentListingAtom, currentListingResourceAtom, currentListingReviewsAtom for read access
 */
export const _fullListingDetailsAtom = atom(
  (get) => get(_fullListingDetailsStateAtom),
  (get, set, newDetails: Record<string, GetListingResponse>) => {
    set(_fullListingDetailsStateAtom, newDetails);
  },
);

/**
 * Internal atom: My licenses (pure state)
 * Private - use myLicensesAtom for read access
 */
export const _myLicensesAtom = atom(
  (get) => get(_myLicensesStateAtom),
  (get, set, newLicenses: StoreLicenseWithDetails[]) => {
    set(_myLicensesStateAtom, newLicenses);
  },
);

/**
 * Internal atom: Available updates (pure state)
 * Private - use availableUpdatesAtom for read access
 */
export const _availableUpdatesAtom = atom(
  (get) => get(_availableUpdatesStateAtom),
  (get, set, newUpdates: GetAvailableUpdatesResponse["updates"]) => {
    set(_availableUpdatesStateAtom, newUpdates);
  },
);

// ============================================================================
// PUBLIC READ-ONLY ATOMS
// ============================================================================

/**
 * Public atom: All store listings (read-only)
 */
export const storeListingsAtom = atom((get) => get(_storeListingsAtom));

/**
 * Public atom: My licenses (read-only)
 */
export const myLicensesAtom = atom((get) => get(_myLicensesAtom));

/**
 * Public atom: Available updates (read-only)
 */
export const availableUpdatesAtom = atom((get) => get(_availableUpdatesAtom));

// ============================================================================
// CURRENT LISTING ATOMS
// ============================================================================

/**
 * Current listing ID
 */
export const currentListingIdAtom = atom<string | null>(null);

/**
 * Current listing (derived from currentListingIdAtom)
 * Returns the full listing details with resource and reviews
 */
export const currentListingAtom = atom((get) => {
  const id = get(currentListingIdAtom);
  if (!id) return null;

  const fullDetails = get(_fullListingDetailsAtom);
  return fullDetails[id]?.listing || null;
});

/**
 * Current listing resource data (agent or knowledge base)
 */
export const currentListingResourceAtom = atom((get) => {
  const id = get(currentListingIdAtom);
  if (!id) return null;

  const fullDetails = get(_fullListingDetailsAtom);
  return fullDetails[id]?.resource || null;
});

/**
 * Current listing reviews
 */
export const currentListingReviewsAtom = atom((get) => {
  const id = get(currentListingIdAtom);
  if (!id) return [];

  const fullDetails = get(_fullListingDetailsAtom);
  return fullDetails[id]?.reviews || [];
});

// ============================================================================
// FILTER & PAGINATION ATOMS
// ============================================================================

/**
 * Store filters
 */
export const storeFiltersAtom = atom<{
  resourceType: "all" | "agent" | "knowledge_base";
  specialty?: string;
  usageContext?: string;
  operations: string[];
  patientTypes: string[];
  tags: string[];
  priceMin: number;
  priceMax: number;
  search: string;
  sortBy: "price" | "rating" | "sales" | "recent";
}>({
  resourceType: "all",
  operations: [],
  patientTypes: [],
  tags: [],
  priceMin: 0,
  priceMax: 200,
  search: "",
  sortBy: "recent",
});

/**
 * Store pagination
 */
export const storePaginationAtom = atom<{
  currentPage: number;
  totalCount: number;
  limit: number;
}>({
  currentPage: 1,
  totalCount: 0,
  limit: 20,
});

// ============================================================================
// STATUS & ERROR ATOMS
// ============================================================================

/**
 * Loading status for store listings
 */
export const storeListingsStatusAtom = atom<AtomStatus>("init");

/**
 * Loading status for my licenses
 */
export const myLicensesStatusAtom = atom<AtomStatus>("init");

/**
 * Loading status for available updates
 */
export const availableUpdatesStatusAtom = atom<AtomStatus>("init");

/**
 * Loading status for current listing
 */
export const currentListingStatusAtom = atom<AtomStatus>("init");

/**
 * Error for store listings
 */
export const storeListingsErrorAtom = atom<AtomError>(null);

/**
 * Error for my licenses
 */
export const myLicensesErrorAtom = atom<AtomError>(null);

/**
 * Error for available updates
 */
export const availableUpdatesErrorAtom = atom<AtomError>(null);

/**
 * Error for current listing
 */
export const currentListingErrorAtom = atom<AtomError>(null);
